/* eslint-disable no-case-declarations */

import { REPAIR_ORDER_MEDIA_TYPE } from 'app/SchedulerDashboard/constants';

const initialState = {
  conditions: [],
  coupons: [],
  error: null, // error object
  gettingRO: false, // can be used to indicate when the app is fetching latest data
  loading: false, // shows fullscreen loader and prevents user interaction
  paymentFormLoaded: false,
  payments: [],
  payment: null,
  repairOrder: null,
  repairOrderQuote: null,
  reviewSites: [], // if this has length and a rating has been submitted, show this list
  ros: [], // used for showing a customer's RO history
  settings: null, // app settings - note that all values are strings, including numbers and booleans
  trims: [],
  videos: [],
  mpiMedia: [],

  // Demo
  demo: null,

  // Rewards
  customerRewardsTier: {},
  rewardsTiers: [],

  // Status
  mpiAvailable: false,
  paymentAvailable: false,
  paymentPaid: false,
  quoteAvailable: false,
  quoteSubmitted: false,
  videoAvailable: false,
  viewedMpi: false,
  viewedUVEye: false,
  viewedQuote: false,
  viewedVideo: false,
  visitAuthorized: false,

  isMpiVisible: false,
  isQuoteVisible: false,
  visibleVideoId: '', // ID of video showing in modal

  appraisalRequested: false, // show the request appraisal card and allow request to be submitted
  financeRequested: false, // show the request finance card and allow request to be submitted

  // Waiver
  waiverVerbiage: null, // waiver text is stamped onto RO *at time of acknowledgement* or something like that
  waiverActivateAuthMessage: false, // false bypasses waiver card (via Settings)

  invalidQuoteETagMessage: null, // if current client has an out-of-date RO and has submitted a POST/PUT request, this will populate with the message to display
};

const receiveSettings = (state, action, callback) => {
  const settings = action.data.reduce((acc, setting) => {
    acc[setting.key] = setting.value;
    return acc;
  }, {});

  settings.hasPayments =
    settings.nmi === '1' || settings.heartland === '1' || settings.globalPay === '1' ? '1' : '0';

  const additionalParams = callback(settings);

  return { ...state, settings, ...additionalParams };
};

const dashboardReducer = (state = initialState, action) => {
  const { data } = action;
  switch (action.type) {
    case 'HANDLE_ERROR':
      return { ...state, loading: false, error: action.error };
    case 'DISMISS_ERROR':
      return { ...state, loading: false, error: null };
    case 'NOW_LOADING':
      return { ...state, loading: true };
    case 'END_LOADING':
      return { ...state, loading: false };
    case 'TOGGLE_MPI':
      return { ...state, isMpiVisible: !state.isMpiVisible };
    case 'TOGGLE_QUOTE':
      return {
        ...state,
        viewedQuote: !state.isQuoteVisible || state.viewedQuote,
        isQuoteVisible: !state.isQuoteVisible,
      };
    // Unconditional close, for error paths that must not re-open an already-closed quote.
    case 'CLOSE_QUOTE':
      return { ...state, isQuoteVisible: false };
    // Records that the customer opened their quote without touching visibility. The route-based
    // experience needs the `viewedQuote` latch that TOGGLE_QUOTE carries, but must not set
    // isQuoteVisible — that would render the legacy modal underneath the new screen.
    case 'QUOTE_VIEWED':
      return { ...state, viewedQuote: true };
    case 'TOGGLE_VIDEO':
      return {
        ...state,
        visibleVideoId: state.visibleVideoId ? '' : action.videoId,
      };
    case 'VIDEO_VIEWED': {
      const videos = state.videos.map((video) => {
        return video.id === action.video.id ? { ...action.video, status: 'Viewed' } : video;
      });
      return {
        ...state,
        videos,
      };
    }
    case 'TOGGLE_WAIVER':
      return { ...state };
    case 'TOGGLE_REVIEW':
      return { ...state };
    case 'RECEIVE_QUOTE': {
      return { ...state, repairOrderQuote: data };
    }
    case 'GET_ROS':
      return { ...state, loading: true };
    case 'RECEIVE_ROS':
      return { ...state, loading: false, ros: data.results };
    case 'GET_RO':
      return {
        ...state,
        // Show loader overlay only if state hasn't been hydrated yet
        loading: !state.repairOrder,
        gettingRO: true,
      };
    case 'RECEIVE_RO':
      const viewedQuote = !!data.repairOrderQuote?.dateCustomerViewed;
      const quoteSubmitted = !!data.repairOrderQuote?.dateCompleted;
      const allowedQuoteStatuses = ['Sent', 'Viewed', 'Complete', 'Completed', 'Confirmed'];
      // ISA-4169: once a quote has been sent to the customer, keep it visible even if a later
      // round pushes the aggregate quoteStatus back to an in-progress state (e.g. "Parts In
      // Progress" after adding a second-round recommendation). The backend already restricts
      // the returned recommendations to sent-only (ISA-4080), so an in-progress round stays
      // hidden at the recommendation level; gating the whole card on quoteStatus alone would
      // wrongly hide the already-sent recommendations too.
      const quoteHasBeenSent = !!data.repairOrderQuote?.dateSent;
      const quoteAvailable =
        !!data.repairOrderQuote?.id &&
        (allowedQuoteStatuses.includes(data.quoteStatus) || quoteHasBeenSent);
      const quoteComingSoonStatuses = [
        'Advisor In Progress',
        'Technician In Progress',
        'Parts In Progress',
      ];
      const quoteComingSoon = quoteComingSoonStatuses.includes(data.quoteStatus);

      // Group previously approved recommendations with pre-approved to make them read-only
      if (data.repairOrderQuote?.repairOrderQuoteRecommendations) {
        data.repairOrderQuote.repairOrderQuoteRecommendations =
          data.repairOrderQuote.repairOrderQuoteRecommendations.map((rec) => {
            return {
              ...rec,
              previouslyApproved: !!rec.approved,
            };
          });
      }

      const viewedMpi = !!data.repairOrderMPI?.dateViewed;
      const allowedMpiStatuses = ['Sent', 'Viewed', 'Complete'];
      const mpiAvailable = !!data.repairOrderMPI?.id && allowedMpiStatuses.includes(data.mpiStatus);

      const viewedVideo = data.videoStatus === 'Viewed';
      const allowedVideoStatuses = ['Sent', 'Viewed'];
      const videoAvailable = allowedVideoStatuses.includes(data.videoStatus);

      const paidStatuses = ['Confirmed', 'Paid'];
      const paymentPaid = paidStatuses.includes(data.paymentStatus);
      const allowedPaymentStatuses = ['Confirmed', 'Paid', 'Sent', 'Viewed'];
      const paymentAvailable = allowedPaymentStatuses.includes(data.paymentStatus);
      const paymentComingSoon = data.paymentStatus === 'Created';

      if (data.primaryAdvisor?.phone) {
        data.primaryAdvisor.phone = data.primaryAdvisor.phone.replace(/\D/g, '') || '';
      }

      return {
        ...state,
        gettingRO: false,
        loading: false,

        repairOrder: data,
        waiverVerbiage: data.waiverVerbiage,

        // Status
        mpiAvailable,
        paymentAvailable,
        paymentComingSoon,
        paymentPaid,
        quoteAvailable,
        quoteComingSoon,
        quoteSubmitted,
        videoAvailable,
        viewedMpi,
        viewedQuote,
        viewedVideo,
        visitAuthorized: !!data.waiverSignature,
        appraisalRequested: false,
        financeRequested: false,
      };
    case 'RECEIVE_COUPONS':
      return { ...state, coupons: action.data };
    case 'RECEIVE_DEMO':
      return { ...state, demo: action.data };
    case 'RECEIVE_REWARDS_TIERS':
      // Have to sort and mutate to make these usable
      const rewardsTiersSorted = action.data.sort((tierA, tierB) => {
        return tierA.minimumSpend - tierB.minimumSpend;
      });
      const rewardsTiers = rewardsTiersSorted.map((tier, i) => {
        // Integers only, regardless of what's in DB
        tier.minimumSpend = Math.round(tier.minimumSpend);

        // Be careful with maximum spend - there are some edge cases
        // - No tiers with $1 minimum spend allowed
        // - No tiers with the same minimum spend
        // - Tiers with $1 increments will look weird in At a Glance
        tier.maximumSpend = Math.round(rewardsTiersSorted[i + 1]?.minimumSpend) - 1 || null;
        return tier;
      });

      // Never get rewards tiers before receiving RO and verifying customer has rewards
      // eslint-disable-next-line
      const { points } = state.repairOrder.primaryCustomer?.customerRewards;
      // Find customer's tier
      const customerRewardsTier = rewardsTiers.find((tier) => {
        return (
          points >= tier.minimumSpend && (points <= tier.maximumSpend || tier.maximumSpend === null)
        );
      });

      return { ...state, customerRewardsTier, rewardsTiers };
    case 'RECEIVE_SETTINGS':
      return receiveSettings(state, action, (settings) => {
        return {
          waiverActivateAuthMessage:
            settings?.waiverActivateAuthMessage === '1' ||
            settings?.waiverActivateAuthMessage === 'true',
        };
      });
    case 'RECEIVE_VIDEOS':
      const videos = Array.isArray(action.data) ? action.data.reverse() : [];

      return { ...state, loading: false, videos };
    case 'RECEIVE_MPI_MEDIA':
      action.data.sort((a, b) => {
        if (a.type === REPAIR_ORDER_MEDIA_TYPE.video && b.type !== REPAIR_ORDER_MEDIA_TYPE.video) {
          return -1;
        }
        if (a.type !== REPAIR_ORDER_MEDIA_TYPE.video && b.type === REPAIR_ORDER_MEDIA_TYPE.video) {
          return 1;
        }

        return b.id - a.id;
      });
      return { ...state, loading: false, mpiMedia: action.data };
    case 'APPRAISAL_REQUESTED':
      return { ...state, loading: false, appraisalRequested: true };
    case 'FINANCE_REQUESTED':
      return { ...state, loading: false, financeRequested: true };
    case 'VISIT_AUTHORIZED':
      return { ...state, loading: false, visitAuthorized: true };
    case 'QUOTE_COMPLETED':
      return {
        ...state,
        loading: false,
        quoteSubmitted: true,
        isQuoteVisible: false,
      };
    case 'PAYMENT_FORM_ERRORED':
      return { ...state, paymentFormLoaded: false };
    case 'PAYMENT_FORM_LOADED':
      return { ...state, paymentFormLoaded: true };
    case 'GET_PAYMENT':
      return { ...state, payment: data, loading: true };
    case 'RECEIVE_PAYMENT':
      return { ...state, payment: data, loading: false };
    case 'MAKE_PAYMENT':
      return { ...state, loading: true };
    case 'PAYMENT_SUCCESS':
      return { ...state, payments: [], loading: false };
    case 'RECEIVE_PAYMENTS':
      return { ...state, payments: action.data, loading: false };
    case 'RECEIVE_CONDITIONS':
      return { ...state, conditions: data };
    case 'RECEIVE_TRIM':
      return { ...state, trims: data };
    case 'START_POLLING_RO':
      return { ...state, roPollingInterval: action.roPollingInterval };
    case 'STOP_POLLING_RO':
      const { roPollingInterval } = state;
      clearInterval(roPollingInterval);

      return { ...state, roPollingInterval: null };
    // login() dispatches this on every mount of the /:linkHash route, including the mount that
    // happens when a customer returns from /:linkHash/estimate — preserve viewedQuote or that
    // return trip re-opens the auto-navigate trap it exists to prevent.
    case 'RESET_DASHBOARD_STATE':
      return { ...initialState, viewedQuote: state.viewedQuote };
    case 'SET_ETAG_ERROR_MESSAGE':
      return { ...state, invalidQuoteETagMessage: data };
    default:
      return { ...state };
  }
};

export default dashboardReducer;
